import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  countryParamPresent,
  useLandingCountryDetection,
} from "../../roofingLight/landingCountryDetect";
import { landingMarket } from "../../roofingLight/landingMarket";
import { testDriveAddressForCountry } from "../../roofingLight/testDrive/testDriveAddress";
import {
  TEST_DRIVE_SERVICE,
  buildTestDriveLead,
  leadCountryForTestDrive,
} from "../../roofingLight/testDrive/testDriveLead";
import {
  TEST_DRIVE_PLAYBACK_MS,
  testDrivePlaybackFrame,
  withSubmittedPhone,
} from "../../roofingLight/testDrive/testDrivePlayback";
import { TEST_DRIVE } from "../shared/testDriveContent.js";

function backendUrl() {
  return (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
}

const TestDriveSection = ({ onOpenModal = () => {} }) => {
  const detection = useLandingCountryDetection();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [playback, setPlayback] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const submittingRef = useRef(false);
  const [resolvedCountry, setResolvedCountry] = useState(null);

  useEffect(() => {
    if (countryParamPresent(searchParams)) return undefined;
    let cancelled = false;
    detection.whenSettled().then((detected) => {
      if (!cancelled) {
        setResolvedCountry(leadCountryForTestDrive(searchParams, detected));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [detection, searchParams]);

  const displayCountry = countryParamPresent(searchParams)
    ? leadCountryForTestDrive(searchParams, null)
    : resolvedCountry;
  const displayAddress = testDriveAddressForCountry(
    displayCountry,
    landingMarket(displayCountry),
  );

  useEffect(() => {
    if (!playback) return undefined;
    let frameId = 0;
    const tick = () => {
      const elapsed = performance.now() - playback.startedAt;
      setElapsedMs(elapsed);
      if (elapsed < TEST_DRIVE_PLAYBACK_MS) frameId = requestAnimationFrame(tick);
    };
    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [playback]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submittingRef.current || playback) return;
    if (!name.trim() || !email.trim() || !phone.trim()) {
      setError(TEST_DRIVE.errorFillAll);
      return;
    }
    setError(null);
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const detectedCountry = countryParamPresent(searchParams)
        ? null
        : await detection.whenSettled();
      const country = leadCountryForTestDrive(searchParams, detectedCountry);
      const market = landingMarket(country);
      if (market == null) {
        setError(TEST_DRIVE.errorUnsupportedRegion);
        return;
      }
      const lead = buildTestDriveLead({
        name,
        phone,
        email,
        country,
        pageUrl: window.location.href,
        search: searchParams,
      });
      if (lead == null) {
        setError(TEST_DRIVE.errorFillAll);
        return;
      }
      const url = backendUrl();
      if (!url) {
        setError(TEST_DRIVE.errorTryAgain);
        return;
      }
      const response = await fetch(`${url}/api/marketing/roofing-light-test-drive`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ market, lead }),
      });
      if (response.ok) {
        setElapsedMs(0);
        setPlayback({ phone: phone.trim(), startedAt: performance.now() });
        setError(null);
      } else {
        setError(TEST_DRIVE.errorTryAgain);
      }
    } catch {
      setError(TEST_DRIVE.errorTryAgain);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const fieldsDisabled = submitting || playback != null;
  const frame = testDrivePlaybackFrame(playback ? elapsedMs : 0);
  const visibleLogs = playback ? TEST_DRIVE.logs.slice(0, frame.visibleCount) : [];

  return (
    <section className="cursor-section roofing-test-drive" data-testid="roofing-test-drive-section">
      <div className="cursor-container roofing-test-drive-inner">
        <p className="roofing-test-drive-badge">{TEST_DRIVE.badge}</p>
        <h2 className="roofing-test-drive-title" data-testid="roofing-test-drive-headline">
          {TEST_DRIVE.headlineLead}{" "}
          <span className="roofing-test-drive-gain">{TEST_DRIVE.headlineGain}</span>{" "}
          {TEST_DRIVE.headlineTail}
        </h2>
        <p className="roofing-test-drive-body" data-testid="roofing-test-drive-body">
          {TEST_DRIVE.body}
        </p>

        <form
          className="roofing-test-drive-form"
          onSubmit={handleSubmit}
          data-testid="roofing-test-drive-form"
        >
          <div className="roofing-test-drive-meta">
            <p className="roofing-test-drive-meta-line" data-testid="roofing-test-drive-service">
              <span className="roofing-test-drive-meta-label">{TEST_DRIVE.serviceLabel}:</span>{" "}
              {TEST_DRIVE_SERVICE}
            </p>
            <p className="roofing-test-drive-meta-line" data-testid="roofing-test-drive-address">
              <span className="roofing-test-drive-meta-label">{TEST_DRIVE.addressLabel}:</span>{" "}
              {displayAddress}
            </p>
          </div>

          <label className="sr-only" htmlFor="roofing-test-drive-name">
            {TEST_DRIVE.namePlaceholder}
          </label>
          <input
            id="roofing-test-drive-name"
            name="name"
            type="text"
            autoComplete="name"
            required
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={TEST_DRIVE.namePlaceholder}
            className="roofing-test-drive-input"
            disabled={fieldsDisabled}
            data-testid="roofing-test-drive-name"
          />

          <label className="sr-only" htmlFor="roofing-test-drive-email">
            {TEST_DRIVE.emailPlaceholder}
          </label>
          <input
            id="roofing-test-drive-email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder={TEST_DRIVE.emailPlaceholder}
            className="roofing-test-drive-input"
            disabled={fieldsDisabled}
            data-testid="roofing-test-drive-email"
          />

          <label className="sr-only" htmlFor="roofing-test-drive-phone">
            {TEST_DRIVE.phonePlaceholder}
          </label>
          <input
            id="roofing-test-drive-phone"
            name="phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            required
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            placeholder={TEST_DRIVE.phonePlaceholder}
            className="roofing-test-drive-input"
            disabled={fieldsDisabled}
            data-testid="roofing-test-drive-phone"
          />

          <button
            type="submit"
            className="roofing-test-drive-cta"
            disabled={fieldsDisabled}
            data-testid="roofing-test-drive-cta"
          >
            {submitting ? TEST_DRIVE.ctaSending : TEST_DRIVE.cta}
          </button>
        </form>

        {error ? (
          <p className="roofing-test-drive-error" data-testid="roofing-test-drive-error">
            {error}
          </p>
        ) : null}

        <p className="roofing-test-drive-timer" data-testid="roofing-test-drive-timer" aria-live="polite">
          <span aria-hidden="true">⏱️ </span>
          {TEST_DRIVE.timerPrefix}{" "}
          <span className="roofing-test-drive-timer-value">{frame.clock}</span>
        </p>

        {playback ? (
          <ul className="roofing-test-drive-statuses" data-testid="roofing-test-drive-statuses">
            {visibleLogs.map((line) => (
              <li key={line.time} className="roofing-test-drive-status">
                <span className="roofing-test-drive-status-time">{line.time}</span>{" "}
                {withSubmittedPhone(line.event, playback.phone)}
              </li>
            ))}
            {frame.showFinal ? (
              <li className="roofing-test-drive-status roofing-test-drive-status-final">
                <span className="roofing-test-drive-status-bullet" aria-hidden="true">●</span>{" "}
                {TEST_DRIVE.finalStatus}
              </li>
            ) : null}
          </ul>
        ) : null}

        <div className="roofing-test-drive-nudge" data-testid="roofing-test-drive-nudge">
          <p className="roofing-test-drive-nudge-copy">{TEST_DRIVE.nudge}</p>
          <button
            type="button"
            className="roofing-test-drive-nudge-cta"
            onClick={onOpenModal}
            data-testid="roofing-test-drive-nudge-cta"
          >
            <span>{TEST_DRIVE.nudgeCta}</span>
            <span aria-hidden="true"> ↗</span>
          </button>
        </div>

        <p className="roofing-test-drive-disclosure" data-testid="roofing-test-drive-disclosure">
          <span aria-hidden="true">✓ </span>
          {TEST_DRIVE.disclosure}
        </p>
      </div>
    </section>
  );
};

export default TestDriveSection;
